import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTeam = () => {
  const { getAuthHeader } = useAuth();
  const [team, setTeam] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    bio: '',
    image_url: '',
    linkedin: ''
  });

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeam(response.data);
    } catch (error) {
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData(member);
    } else {
      setEditingMember(null);
      setFormData({
        name: '',
        position: '',
        bio: '',
        image_url: '',
        linkedin: ''
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingMember(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingMember) {
        await axios.put(`${API}/team/${editingMember.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Team member updated successfully!');
      } else {
        await axios.post(`${API}/team`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Team member added successfully!');
      }
      fetchTeam();
      closeModal();
    } catch (error) {
      toast.error('Failed to save team member');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;

    try {
      await axios.delete(`${API}/team/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('Team member deleted successfully!');
      fetchTeam();
    } catch (error) {
      toast.error('Failed to delete team member');
    }
  };

  return (
    <div data-testid="admin-team-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-playfair font-bold text-stone-900 mb-2">Team Members</h1>
          <p className="text-stone-600">Manage your team</p>
        </div>
        <Button
          onClick={() => openModal()}
          data-testid="add-team-button"
          className="bg-emerald-900 hover:bg-emerald-800 text-white px-6 py-3 uppercase tracking-widest text-xs font-bold"
        >
          <Plus className="w-4 h-4 mr-2" /> ADD MEMBER
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {team.map((member) => (
          <div
            key={member.id}
            data-testid={`team-item-${member.id}`}
            className="bg-white border border-stone-200 overflow-hidden"
          >
            <div className="aspect-square overflow-hidden">
              <img
                src={member.image_url}
                alt={member.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <h3 className="text-xl font-playfair font-bold text-stone-900 mb-1">{member.name}</h3>
              <p className="text-amber-600 text-sm uppercase tracking-widest mb-2">{member.position}</p>
              <p className="text-stone-600 text-sm line-clamp-2 mb-4">{member.bio}</p>
              <div className="flex gap-2">
                <Button
                  onClick={() => openModal(member)}
                  data-testid={`edit-team-${member.id}`}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button
                  onClick={() => handleDelete(member.id)}
                  data-testid={`delete-team-${member.id}`}
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {team.length === 0 && (
        <div className="text-center py-20 bg-white border border-stone-200">
          <p className="text-stone-500">No team members yet. Add your first team member!</p>
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent className="max-w-lg" data-testid="team-form-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-playfair font-bold">
              {editingMember ? 'Edit Team Member' : 'Add Team Member'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              name="name"
              placeholder="Full Name *"
              value={formData.name}
              onChange={handleChange}
              required
              data-testid="team-name-input"
            />
            <Input
              name="position"
              placeholder="Position *"
              value={formData.position}
              onChange={handleChange}
              required
              data-testid="team-position-input"
            />
            <Textarea
              name="bio"
              placeholder="Bio *"
              value={formData.bio}
              onChange={handleChange}
              required
              rows={4}
              data-testid="team-bio-input"
            />
            <Input
              name="image_url"
              placeholder="Image URL *"
              value={formData.image_url}
              onChange={handleChange}
              required
              data-testid="team-image-input"
            />
            <Input
              name="linkedin"
              placeholder="LinkedIn URL (Optional)"
              value={formData.linkedin}
              onChange={handleChange}
              data-testid="team-linkedin-input"
            />
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                data-testid="team-submit-button"
                className="flex-1 bg-emerald-900 hover:bg-emerald-800"
              >
                {editingMember ? 'UPDATE MEMBER' : 'ADD MEMBER'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                data-testid="team-cancel-button"
              >
                CANCEL
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTeam;